/**
 * Cookie Consent Banner Component (GDPR Compliant)
 *
 * Displays a cookie consent banner and manages user preferences.
 * Stores consent in localStorage and provides granular control.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface CookiePreferences {
  essential: boolean; // Always true, cannot be disabled
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  consentGiven: boolean;
  consentDate: string | null;
}

const CONSENT_KEY = 'cookie_consent';

const defaultPreferences: CookiePreferences = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
  consentGiven: false,
  consentDate: null,
};

export function getCookiePreferences(): CookiePreferences {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Invalid JSON, return defaults
  }
  return defaultPreferences;
}

export function saveCookiePreferences(preferences: CookiePreferences): void {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences));

  // Dispatch event for other components to react
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: preferences }));
}

export function hasConsentFor(category: keyof Omit<CookiePreferences, 'consentGiven' | 'consentDate'>): boolean {
  const prefs = getCookiePreferences();
  return prefs[category];
}

export default function CookieConsent() {
  const { t } = useTranslation('common');
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    const stored = getCookiePreferences();
    setPreferences(stored);

    // Show banner if consent not given
    if (!stored.consentGiven) {
      // Small delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const newPrefs: CookiePreferences = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      consentGiven: true,
      consentDate: new Date().toISOString(),
    };
    saveCookiePreferences(newPrefs);
    setPreferences(newPrefs);
    setShowBanner(false);
  };

  const handleAcceptEssential = () => {
    const newPrefs: CookiePreferences = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      consentGiven: true,
      consentDate: new Date().toISOString(),
    };
    saveCookiePreferences(newPrefs);
    setPreferences(newPrefs);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const newPrefs: CookiePreferences = {
      ...preferences,
      essential: true, // Always true
      consentGiven: true,
      consentDate: new Date().toISOString(),
    };
    saveCookiePreferences(newPrefs);
    setPreferences(newPrefs);
    setShowBanner(false);
    setShowDetails(false);
  };

  const togglePreference = (key: 'functional' | 'analytics' | 'marketing') => {
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        {/* Main Banner */}
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('cookies.title')}
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                {t('cookies.description')}{' '}
                <Link to="/privacy" className="text-blue-600 hover:underline">
                  {t('cookies.privacyPolicy')}
                </Link>
              </p>

              {/* Detailed Options */}
              {showDetails && (
                <div className="mt-4 space-y-4 border-t pt-4">
                  {/* Essential */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{t('cookies.essentialCookies')}</h4>
                      <p className="text-sm text-gray-500">
                        {t('cookies.essentialDescription')}
                      </p>
                    </div>
                    <div className="ltr:ml-4 rtl:mr-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {t('cookies.alwaysActive')}
                      </span>
                    </div>
                  </div>

                  {/* Functional */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{t('cookies.functionalCookies')}</h4>
                      <p className="text-sm text-gray-500">
                        {t('cookies.functionalDescription')}
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => togglePreference('functional')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences.functional ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences.functional ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{t('cookies.analyticsCookies')}</h4>
                      <p className="text-sm text-gray-500">
                        {t('cookies.analyticsDescription')}
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => togglePreference('analytics')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences.analytics ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences.analytics ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Marketing */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{t('cookies.marketingCookies')}</h4>
                      <p className="text-sm text-gray-500">
                        {t('cookies.marketingDescription')}
                      </p>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => togglePreference('marketing')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          preferences.marketing ? 'bg-blue-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            preferences.marketing ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Buttons */}
          <div className="mt-6 flex flex-wrap gap-3 ltr:justify-end rtl:justify-start">
            {!showDetails ? (
              <>
                <button
                  onClick={() => setShowDetails(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {t('cookies.customize')}
                </button>
                <button
                  onClick={handleAcceptEssential}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {t('cookies.essentialOnly')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  {t('cookies.acceptAll')}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {t('cookies.back')}
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  {t('cookies.savePreferences')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to check cookie consent
 */
export function useCookieConsent() {
  const [preferences, setPreferences] = useState<CookiePreferences>(getCookiePreferences);

  useEffect(() => {
    const handleChange = (e: CustomEvent<CookiePreferences>) => {
      setPreferences(e.detail);
    };

    window.addEventListener('cookieConsentChanged', handleChange as EventListener);
    return () => window.removeEventListener('cookieConsentChanged', handleChange as EventListener);
  }, []);

  return preferences;
}
